import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid-pro';

import { TitleH3 } from 'components/Title/TitleH3';
import { Row } from '../../../components/Table/DashboardTable';
import CreateButton from '../../../components/Buttons/CreateButton';

import styles from './styles.module.scss';
import requestService from '../../../store/request/service';
import { paths } from '../DriversPage';
import {
  DIALOG_TYPES,
  useGlobalDialogContext,
} from 'App/components/Layouts/Dashboard/dialog/GlobalDialog';
import { IReviewDocument } from '../../../common/request/interfaces/reviewDocument';
import { DocumentReviewContainer } from '../../../features/Dashboard/DocumentView/DocumentReviewContainer';
import { DashboardDataGridTable } from '../../../components/Table/DashboardDataGridTable';
import { readyToReviewColumns } from '../../../features/Dashboard/RequestTable/columns/ready-to-review.columns';
import { dataGridPaginationBuilder } from '../../../store/api/query-builders/data-grid/pagination/data-grid-pagination.builder';
import { myRequestsColumns } from '../../../features/Dashboard/RequestTable/columns/my-requests.columns';
import {
  buildFilterParamsFromUrlParameters,
  buildPaginationModelFromUrlParameters,
  buildSortModelFromUrlParameters,
  buildTableUrlParameters,
} from '../../../components/Table/DashboardDataGridTable/utils/url-params.util';
import { CompanyContext } from '../../../index';
import { filterByName } from '../../../functions/filterByName';

const PAGE_SIZE = 100;

const READY_TO_REVIEW_TABLE = 't1';
const MY_REQUESTS_TABLE = 't2';

export const DashboardPage = () => {
  const company = useContext(CompanyContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showDialog } = useGlobalDialogContext();

  const [openReviewDialog, setOpenReviewDialog] = useState<boolean>(false);
  const [documentRequestId, setDocumentRequestId] = useState<string>('');

  const [searchValueReadyToReview, setSearchValueReadyToReview] =
    useState<string>(
      buildFilterParamsFromUrlParameters(
        READY_TO_REVIEW_TABLE,
        searchParams.toString()
      ).get('candidateFullName') || ''
    );

  const [searchValueMyRequest, setSearchValueMyRequest] = useState<string>(
    buildFilterParamsFromUrlParameters(
      MY_REQUESTS_TABLE,
      searchParams.toString()
    ).get('candidateFullName') || ''
  );

  const [candidateDocumentReviewId, setCandidateDocumentReviewId] =
    useState<string>('');

  const [requestReadyToReviewListQuery, setRequestReadyToReviewListQuery] =
    useState<string>(
      dataGridPaginationBuilder({ page: 0, pageSize: PAGE_SIZE }) +
        '&sort[submittedAt]=1'
    );

  const [myRequestListQuery, setMyRequestListQuery] = useState<string>(
    dataGridPaginationBuilder({ page: 0, pageSize: PAGE_SIZE }) +
      '&sort[submittedAt]=1'
  );

  const handleOpenCreateRequestDialog = () => {
    showDialog(DIALOG_TYPES.CREATE_REQUEST_DIALOG, {});
  };

  const { data: readyToReview } =
    requestService.endpoints.getRequestReadyToReviewList.useQuery({
      query: [
        requestReadyToReviewListQuery.toString(),
        filterByName(searchValueReadyToReview),
      ]
        .filter((el) => !!el)
        .join('&'),
    });

  const { data: myRequests } =
    requestService.endpoints.getMyRequestList.useQuery({
      query: [myRequestListQuery.toString(), filterByName(searchValueMyRequest)]
        .filter((el) => !!el)
        .join('&'),
    });

  const handleSendMessage = (request: IReviewDocument) => {
    showDialog(DIALOG_TYPES.CONFIRM_REQUEST_DIALOG, {
      request,
    });
  };

  const handleReview = async (id: string): Promise<void> => {
    setOpenReviewDialog(true);
    setDocumentRequestId(id);
  };

  const handleGoToProfile = (id: string) => {
    navigate(paths.DashboardDriversReviewPage.replace(':id', id));
  };

  const handleOpenReviewDialog = (v: boolean) => {
    setCandidateDocumentReviewId('');
    setOpenReviewDialog(v);
  };

  const handleSearchReadyToReview = (v: string) => {
    setSearchValueReadyToReview(v);
  };

  const handleSearchMyRequest = (v: string) => {
    setSearchValueMyRequest(v);
  };

  const myRequestsColumnsWithActions = React.useMemo<GridColDef<Row>[]>(
    () =>
      myRequestsColumns({
        sendReminder: handleSendMessage,
        onManage: handleGoToProfile,
        employeeIdLabel: company?.labels?.employeeId,
        searchValue: searchValueMyRequest,
        onSearchChange: handleSearchMyRequest,
      }),
    [handleSendMessage]
  );

  useEffect(() => {
    setSearchParams({
      ...buildTableUrlParameters(
        READY_TO_REVIEW_TABLE,
        [requestReadyToReviewListQuery, filterByName(searchValueReadyToReview)]
          .filter((el) => !!el)
          .join('&')
      ),
      ...buildTableUrlParameters(
        MY_REQUESTS_TABLE,
        [myRequestListQuery, filterByName(searchValueMyRequest)]
          .filter((el) => !!el)
          .join('&')
      ),
    });
  }, [
    requestReadyToReviewListQuery,
    myRequestListQuery,
    searchValueReadyToReview,
    searchValueMyRequest,
  ]);

  return (
    <div className={styles.root}>
      <div style={{ width: '100%' }}>
        <TitleH3>Ready to Review</TitleH3>
        <DashboardDataGridTable
          columns={readyToReviewColumns({
            employeeIdLabel: company?.labels?.employeeId,
            searchValue: searchValueReadyToReview,
            onSearchChange: handleSearchReadyToReview,
          })}
          rows={readyToReview?.documents || []}
          totalRows={readyToReview?.totalRows || 0}
          onRowClick={handleReview}
          isLoading={false}
          setQuery={setRequestReadyToReviewListQuery}
          initialState={{
            pagination: buildPaginationModelFromUrlParameters(
              READY_TO_REVIEW_TABLE,
              searchParams.toString(),
              PAGE_SIZE
            ),
            sorting: {
              // @ts-ignore
              sortModel: buildSortModelFromUrlParameters(
                READY_TO_REVIEW_TABLE,
                searchParams.toString() ||
                  `&${READY_TO_REVIEW_TABLE}[sort][submittedAt]=1`
              ),
            },
          }}
        />
      </div>
      <div style={{ width: '100%' }}>
        <div className={styles.titleWrapper}>
          <TitleH3>My Requests</TitleH3>
          <CreateButton onClick={handleOpenCreateRequestDialog}>
            Create a New Request
          </CreateButton>
        </div>
        <DashboardDataGridTable
          columns={myRequestsColumnsWithActions}
          rows={myRequests?.documents || []}
          totalRows={myRequests?.totalRows || 0}
          isLoading={false}
          setQuery={setMyRequestListQuery}
          initialState={{
            pagination: buildPaginationModelFromUrlParameters(
              MY_REQUESTS_TABLE,
              searchParams.toString(),
              PAGE_SIZE
            ),
            sorting: {
              // @ts-ignore
              sortModel: buildSortModelFromUrlParameters(
                MY_REQUESTS_TABLE,
                searchParams.toString() ||
                  `&${MY_REQUESTS_TABLE}[sort][submittedAt]=1`
              ),
            },
          }}
        />
      </div>
      <DocumentReviewContainer
        openReviewDialog={openReviewDialog}
        setOpenReviewDialog={handleOpenReviewDialog}
        documentRequestId={documentRequestId}
        selectedCandidateDocumentId={candidateDocumentReviewId}
        setSelectedCandidateDocumentId={setCandidateDocumentReviewId}
      />
    </div>
  );
};
